import React, { useEffect, useRef } from 'react';

/**
 * Talão Cliente Janeira — formato térmico 58mm
 *
 * Props:
 *  - dados: objeto com {
 *      cliente: { nome, id_cartao_rfid, tel?, nif?, tipo? },
 *      pontos: { saldo, valor_euro, validade?, minimo_resgate? },
 *      cashback: {
 *        saldo, saldo_resgatavel, motivo_bloqueio?,
 *        bonus_adesao?: { valor, expira_em, minimo_compra }
 *      },
 *      vales: [{ numero, valor, validade, parceria? }],
 *      perfis_beneficio: [{ nome, regras_texto }],
 *      campanhas_em_vigor: [{
 *        nome, tipo, descricao?, acumular_de?, acumular_ate?,
 *        resgatar_de?, resgatar_ate?, minimo_compra?
 *      }],
 *    }
 *  - onClose: callback para fechar
 *  - autoPrint: se true, dispara window.print() ao montar
 */
export function Talao({ dados, onClose, autoPrint = false }) {
  const printedRef = useRef(false);

  useEffect(() => {
    if (autoPrint && !printedRef.current) {
      printedRef.current = true;
      // pequeno atraso para garantir renderização
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [autoPrint]);

  if (!dados) return null;

  const cli = dados.cliente || {};
  const pontos = dados.pontos || {};
  const cashback = dados.cashback || {};
  const vales = dados.vales || [];
  const perfis = dados.perfis_beneficio || [];
  const campanhas = dados.campanhas_em_vigor || [];
  const bonus = cashback.bonus_adesao;

  const agora = new Date();
  const dataStr = agora.toLocaleDateString('pt-PT');
  const horaStr = agora.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* Estilo de impressão térmica 58mm — só este DIV vai para a impressora */}
      <style>{`
        @media print {
          @page { size: 58mm auto; margin: 0; }
          html, body { background: white !important; margin: 0; padding: 0; }
          body * { visibility: hidden; }
          .talao-print, .talao-print * { visibility: visible; }
          .talao-print {
            position: absolute; left: 0; top: 0;
            width: 58mm; padding: 4mm 3mm 8mm 3mm;
            font-family: 'Courier New', Courier, monospace;
            font-size: 9pt; line-height: 1.25; color: #000;
          }
          .talao-actions { display: none !important; }
        }
        .talao-onscreen {
          width: 58mm; min-height: 80mm; padding: 4mm 3mm 8mm 3mm;
          font-family: 'Courier New', Courier, monospace;
          font-size: 10pt; line-height: 1.3; color: #000;
          background: white; border: 1px dashed #cbd5e1;
          margin: 0 auto; box-shadow: 0 1px 4px rgba(0,0,0,.08);
        }
      `}</style>

      <div className="fixed inset-0 z-[60] bg-black/50 overflow-auto" data-testid="talao-overlay">
        <div className="min-h-screen flex flex-col items-center py-6 px-3">
          {/* Botões (não imprimem) */}
          <div className="talao-actions w-full max-w-[420px] flex gap-2 mb-3 justify-end print:hidden">
            <button
              onClick={() => window.print()}
              className="px-3 py-2 bg-[#00B7B6] text-white rounded text-sm font-medium hover:bg-[#009999]"
              data-testid="talao-btn-imprimir"
            >
              Imprimir
            </button>
            <button
              onClick={onClose}
              className="px-3 py-2 bg-gray-200 text-gray-800 rounded text-sm font-medium hover:bg-gray-300"
              data-testid="talao-btn-fechar"
            >
              Fechar
            </button>
          </div>

          {/* Talão (esta secção é a que imprime) */}
          <div className="talao-print talao-onscreen" data-testid="talao-conteudo">
            {/* Cabeçalho */}
            <div style={{ textAlign: 'center', marginBottom: '4mm' }}>
              <div style={{ fontSize: '14pt', fontWeight: 'bold', letterSpacing: '1px' }}>JANEIRA</div>
              <div style={{ fontSize: '8pt' }}>Talão de Benefícios</div>
              <div style={{ fontSize: '8pt', marginTop: '1mm' }}>{dataStr} {horaStr}</div>
            </div>

            {/* Cliente */}
            <Sep />
            <div style={{ fontWeight: 'bold' }}>{cli.nome || '—'}</div>
            <Linha label="Cartão" valor={cli.id_cartao_rfid || '—'} />
            {cli.nif && <Linha label="NIF" valor={cli.nif} />}
            {cli.tel && <Linha label="Tel" valor={cli.tel} />}
            {cli.tipo && <Linha label="Tipo" valor={cli.tipo} />}

            {/* Pontos */}
            <Sep />
            <Cab>PONTOS</Cab>
            <Linha label="Saldo" valor={`${pontos.saldo ?? 0} pts`} />
            {pontos.valor_euro != null && (
              <Linha label="Equivalente" valor={`${Number(pontos.valor_euro).toFixed(2)} EUR`} />
            )}
            {pontos.minimo_resgate != null && (
              <Linha label="Min. resgate" valor={`${pontos.minimo_resgate} pts`} />
            )}
            {pontos.validade && (
              <div style={{ fontSize: '8pt' }}>Validade: {pontos.validade}</div>
            )}

            {/* Cashback */}
            <Sep />
            <Cab>CASHBACK</Cab>
            <Linha label="Saldo total" valor={`${Number(cashback.saldo ?? 0).toFixed(2)} EUR`} />
            {cashback.saldo_resgatavel != null && (
              <Linha
                label="Resgatavel"
                valor={`${Number(cashback.saldo_resgatavel).toFixed(2)} EUR`}
              />
            )}
            {cashback.motivo_bloqueio && (
              <div style={{ fontSize: '8pt', marginTop: '1mm' }}>{cashback.motivo_bloqueio}</div>
            )}
            {bonus && (
              <div style={{ fontSize: '8pt', marginTop: '1mm' }}>
                Bonus adesao {Number(bonus.valor).toFixed(2)} EUR
                {bonus.expira_em ? ` valido ate ${bonus.expira_em}` : ''}
                {bonus.minimo_compra ? ` (compra >= ${Number(bonus.minimo_compra).toFixed(2)} EUR)` : ''}
              </div>
            )}

            {/* Vales */}
            <Sep />
            <Cab>VALES ({vales.length})</Cab>
            {vales.length === 0 ? (
              <div style={{ fontSize: '8pt' }}>Sem vales ativos</div>
            ) : (
              vales.map((v, i) => (
                <div key={i} style={{ marginTop: '1mm', fontSize: '9pt' }}>
                  <div>#{v.numero || '—'} {v.parceria ? `· ${v.parceria}` : ''}</div>
                  <div style={{ fontSize: '8pt' }}>
                    {Number(v.valor || 0).toFixed(2)} EUR · valido ate {v.validade || '—'}
                  </div>
                </div>
              ))
            )}

            {/* Parcerias */}
            <Sep />
            <Cab>PARCERIAS / PERFIL</Cab>
            {perfis.length === 0 ? (
              <div style={{ fontSize: '8pt' }}>Sem parcerias</div>
            ) : (
              perfis.map((p, i) => (
                <div key={i} style={{ marginTop: '1mm' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{p.nome}</div>
                  {p.regras_texto && (
                    <div style={{ fontSize: '8pt' }}>{p.regras_texto}</div>
                  )}
                </div>
              ))
            )}

            {/* Campanhas em vigor */}
            <Sep />
            <Cab>CAMPANHAS EM VIGOR</Cab>
            {campanhas.length === 0 ? (
              <div style={{ fontSize: '8pt' }}>Sem campanhas ativas</div>
            ) : (
              campanhas.map((c, i) => (
                <div key={i} style={{ marginTop: '1.5mm', fontSize: '9pt' }}>
                  <div style={{ fontWeight: 'bold' }}>{c.nome}</div>
                  {c.tipo && (
                    <div style={{ fontSize: '8pt' }}>{c.tipo}</div>
                  )}
                  {c.descricao && (
                    <div style={{ fontSize: '8pt' }}>{c.descricao}</div>
                  )}
                  {(c.acumular_de || c.acumular_ate) && (
                    <div style={{ fontSize: '8pt' }}>
                      Acumular {c.acumular_de ? `de ${c.acumular_de} ` : ''}
                      {c.acumular_ate ? `ate ${c.acumular_ate}` : ''}
                    </div>
                  )}
                  {(c.resgatar_de || c.resgatar_ate) && (
                    <div style={{ fontSize: '8pt' }}>
                      Resgatar {c.resgatar_de ? `de ${c.resgatar_de} ` : ''}
                      {c.resgatar_ate ? `ate ${c.resgatar_ate}` : ''}
                    </div>
                  )}
                  {c.minimo_compra != null && c.minimo_compra > 0 && (
                    <div style={{ fontSize: '8pt' }}>
                      Compra min. {Number(c.minimo_compra).toFixed(2)} EUR
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Rodapé */}
            <Sep />
            <div style={{ textAlign: 'center', fontSize: '8pt', marginTop: '2mm' }}>
              janeira.pt
            </div>
            <div style={{ textAlign: 'center', fontSize: '7pt', marginTop: '0.5mm' }}>
              Obrigado pela preferencia
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Sep() {
  return (
    <div style={{
      borderTop: '1px dashed #000',
      margin: '2mm 0',
    }} />
  );
}

function Cab({ children }) {
  return (
    <div style={{
      fontWeight: 'bold',
      fontSize: '10pt',
      marginBottom: '1mm',
      letterSpacing: '0.5px',
    }}>{children}</div>
  );
}

function Linha({ label, valor }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '9pt',
    }}>
      <span>{label}</span>
      <span style={{ fontWeight: 'bold' }}>{valor}</span>
    </div>
  );
}

export default Talao;
